import type { ContactSubmissionPayload } from '@/domain/contact';

/**
 * The one network call this application makes.
 *
 * Nothing is sent on page load, on answering a question or on reaching the
 * result. `submitContact` runs exactly once, when someone has filled in the
 * form and pressed the button.
 */

const baseUrl: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export type SubmitOutcome =
  | { status: 'ok' }
  | { status: 'invalid'; message: string; fieldErrors?: Record<string, string[]> }
  | { status: 'rate-limited'; message: string }
  | { status: 'offline'; message: string }
  | { status: 'error'; message: string };

/** ASP.NET Core's ProblemDetails / ValidationProblemDetails. */
interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

const TIMEOUT_MS = 12_000;

export async function submitContact(
  payload: ContactSubmissionPayload,
): Promise<SubmitOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(`${baseUrl}/api/contact-submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (response.ok) return { status: 'ok' };

    if (response.status === 429) {
      return {
        status: 'rate-limited',
        message: "That is a few more tries than expected. Give it a minute and try again.",
      };
    }

    if (response.status === 400 || response.status === 422) {
      const problem = await safeJson<ProblemDetails>(response);
      return {
        status: 'invalid',
        message:
          problem?.detail ??
          problem?.title ??
          'Something in the form was not quite right.',
        fieldErrors: problem?.errors,
      };
    }

    return {
      status: 'error',
      message: "Something went wrong at my end, not yours. Please try again in a moment.",
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return {
        status: 'offline',
        message: 'That took too long. Check your connection and try again.',
      };
    }
    return {
      status: 'offline',
      message: "Could not reach the server. Check your connection and try again.",
    };
  } finally {
    clearTimeout(timer);
  }
}

async function safeJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}
